import { useContext, useState } from "react";
import { API_BASE } from "../config";
import { track } from "../track";
import { toast } from "../ui/Toast";
import { sid, type Extra, type PreviewPiece } from "../types";
import type { Words } from "./copy";
import { PageContext } from "./page";

/** The words and the little picture each extra is offered with, in the order
 *  the row shows them: the hat first, the one every product can wear. */
const LOOKS: { extra: Extra; key: string; icon: string }[] = [
  { extra: "christmas_hat", key: "lookHat", icon: "🎅" },
  { extra: "christmas_sweater", key: "lookSweater", icon: "🧶" },
];

/** Why a tap did not redraw anything, said under the row rather than in a
 *  toast: it stays while they decide what to do about it. */
type Refusal = "busy" | "limit" | "unavailable";
const REFUSAL_KEY: Record<Refusal, string> = {
  busy: "lookBusy",
  limit: "lookLimit",
  unavailable: "lookUnavailable",
};

/** The Christmas look, under one piece's drawing: the Santa hat and the
 *  Christmas sweater, free, each put on or taken off with a tap.
 *
 *  A tap asks /api/extras to redraw this piece wearing exactly what the row
 *  then says. The server answers in one of three ways. It may already have a
 *  drawing of this piece wearing just that, from a moment ago, and put it
 *  back at once (`restored`): the page is read again and the drawing changes,
 *  with nothing drawn and nothing counted. It may start a redraw (202,
 *  `live`): the drawing goes grey and sketches itself again exactly as a
 *  change being fixed does (Preview's startFixing), and "Done!" brings the new
 *  one in about a minute. Or it refuses: something is already being redrawn
 *  (`busy`), this preview has had all the Christmas redraws it can (`limit`,
 *  a few per lead, counted apart from the changes they ask for), or a redraw
 *  cannot happen in front of them just now (`not-live`, `closed`).
 *
 *  Undo is the tap before, taken back: the same request the other way round,
 *  which the server answers by putting the previous drawing back, free and at
 *  once, since it still has it. Offered once something has changed, until
 *  the next tap.
 *
 *  Only while the server offers it for this piece (extrasOffered: in season,
 *  on a page that is sent and not ordered, where a live redraw is possible),
 *  and not while a change of this page is being drawn, which the drawing
 *  above is busy saying. Mounted all the same while it hides, so the Undo it
 *  owes them is still there when the new drawing lands.
 *
 *  The studio's own look ("See what they see") shows the row as the customer
 *  sees it, but its buttons do nothing: a tap there would redraw the
 *  customer's preview on the owner's behalf. */
export default function ChristmasLook({
  piece,
  busy,
  words: t,
  onRedraw,
  onReread,
}: {
  piece: PreviewPiece;
  /** A change of this piece is being drawn, or has just landed. */
  busy: boolean;
  words: Words;
  /** A redraw has started: the drawing goes grey and sketches itself. */
  onRedraw: () => void;
  /** Read the page again: the drawing before was put back, or the server
   *  said the row is not what it should be any more. */
  onReread: () => void;
}) {
  const page = useContext(PageContext);
  const [sending, setSending] = useState<Extra | null>(null);
  const [refused, setRefused] = useState<Refusal | null>(null);
  /** The tap that Undo takes back: that extra, put back the way it was. */
  const [undo, setUndo] = useState<{ extra: Extra; on: boolean } | null>(null);

  const offered = piece.extrasOffered ?? [];
  const worn = piece.extras ?? [];
  const admin = !!page?.admin;
  if (busy || !offered.length || (page?.ordered && !admin)) return null;

  async function send(extra: Extra, on: boolean, how: "toggle" | "undo") {
    if (admin || sending) return;
    setSending(extra);
    setRefused(null);
    try {
      const response = await fetch(`${API_BASE}/api/extras`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: page?.token ?? "", pieceId: sid(piece.id), extra, on }),
      });
      const answer = (await response.json().catch(() => ({}))) as {
        already?: boolean;
        restored?: boolean;
        live?: boolean;
        error?: string;
      };
      if (response.status === 202 || answer.live) {
        // Undo puts back what was there before this tap; an Undo itself
        // leaves nothing more to undo.
        setUndo(how === "undo" ? null : { extra, on: !on });
        onRedraw();
        return;
      }
      if (response.ok) {
        // Already wearing exactly that: the page on screen was behind, and a
        // read brings the row up to date. Nothing was changed, so there is
        // nothing for Undo to take back either.
        setUndo(answer.restored && how !== "undo" ? { extra, on: !on } : null);
        onReread();
        return;
      }
      if (response.status === 429) setRefused("limit");
      else if (answer.error === "busy") setRefused("busy");
      else if (response.status === 409) {
        // Not possible in front of them now, or the season is over: what the
        // page offers is read again, and the row goes if it should.
        setRefused("unavailable");
        onReread();
      } else throw new Error(String(response.status));
    } catch {
      toast(t.t("sendFailed"), "bad");
    } finally {
      setSending(null);
    }
  }

  const looks = LOOKS.filter((look) => offered.includes(look.extra));

  return (
    <div className="look" role="group" aria-labelledby={`look-${piece.id || "one"}`}>
      <p className="look-head small" id={`look-${piece.id || "one"}`}>
        <strong>{t.t("lookTitle")}</strong>
        <span className="look-free">{t.t("lookFree")}</span>
      </p>
      <div className="row row-wrap look-row">
        {looks.map(({ extra, key, icon }) => {
          const on = worn.includes(extra);
          return (
            <button
              key={extra}
              type="button"
              className={`chip${on ? " chip-on" : ""}`}
              aria-pressed={on}
              // Not switched off by "limit": a look already drawn is put back
              // free, before the count is ever asked, so each tap is the
              // server's to answer ("restored", or "limit" again).
              disabled={admin || !!sending}
              onClick={() => {
                track("look.toggled", { extra, on: !on, kind: piece.kind ?? "" });
                void send(extra, !on, "toggle");
              }}
            >
              {sending === extra ? <span className="spin" aria-hidden="true" /> : <span aria-hidden="true">{icon}</span>}
              {t.t(key)}
            </button>
          );
        })}
        {/* Only while the drawing still wears what was tapped: a redraw that
            failed, or went to the studio, left nothing to take back, and an
            Undo then would only be answered "already" for ever. */}
        {undo && offered.includes(undo.extra) && worn.includes(undo.extra) !== undo.on && (
          <button
            type="button"
            className="btn btn-ghost btn-sm look-undo"
            disabled={admin || !!sending}
            onClick={() => {
              track("look.undone", { extra: undo.extra, on: undo.on });
              void send(undo.extra, undo.on, "undo");
            }}
          >
            {t.t("lookUndo")}
          </button>
        )}
      </div>
      {refused ? (
        <p className="tiny look-said" role="status">
          {t.t(REFUSAL_KEY[refused])}
        </p>
      ) : (
        <p className="tiny muted look-said">{t.t("lookDrawing")}</p>
      )}
    </div>
  );
}
